/**
 * I present SimpleGuide — the 4-step Simple view guide (#476), in Vault's spotlight
 * style: page dimmed around a clear cut-out (no outline), card beside it (never covering it; at the
 * bottom on narrow screens). Runs once, the first time Simple view opens on
 * any device and can be replayed; Done or Skip stamps simple_guide_seen on the account.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const STEPS = [
  { k: 'safe', t: 'Safe to spend', b: 'This is what you can spend today and still be fine until payday.' },
  { k: 'log', t: 'Log a spend', b: 'Tap here whenever you buy something. Scan the slip, type it, or just say it. Check the amount, the expense and the shop before Save. You can change every suggestion.' },
  { k: 'month', t: 'Tick what’s paid', b: 'Tap the circle when a bill is paid. Tap it again if you made a mistake.' },
  { k: 'pill', t: 'Show everything', b: 'Want the full Vault? Tap here any time. Nothing is lost, and you can come back.' },
];
const PAD = 8;

/** Explain the four Simple controls without changing budget data; report dismissal to the owner. */
export default function SimpleGuide({ onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardH, setCardH] = useState(240);
  const cardRef = useRef(null);
  const step = STEPS[i];

  const target = useCallback(() => {
    const all = [...document.querySelectorAll(`[data-sv="${step.k}"]`)];
    return all.find((el) => el.getClientRects().length) || null;   // the visible one
  }, [step.k]);

  const measure = useCallback(() => {
    const el = target();
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [target]);

  useEffect(() => {
    const el = target();
    // The This month block is tall: bring its top into view, not its middle.
    if (el) el.scrollIntoView({ block: step.k === 'month' ? 'start' : 'center', behavior: 'auto' });
    const t = setTimeout(measure, 30);
    // Account data and font layout can settle without a scroll or resize event.
    const poll = setInterval(measure, 250);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); clearInterval(poll); window.removeEventListener('scroll', measure, true); window.removeEventListener('resize', measure); };
  }, [i, measure, target, step.k]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
    cardRef.current?.focus();
  }, [i]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(360, vw - 32);
  const narrow = vw < 640;
  const clampL = (x) => Math.min(Math.max(x, 16), vw - w - 16);
  let pos;
  if (!rect) {
    pos = { left: (vw - w) / 2, bottom: 16 };
  } else if (narrow) {
    // Bottom card, unless the target sits down there — then the top.
    pos = rect.top + rect.height + PAD + 12 > vh - cardH - 16 && rect.top > cardH + 32
      ? { left: 16, top: 16 } : { left: 16, bottom: 16 };
  } else if (vh - (rect.top + rect.height + PAD) >= cardH + 24) {
    pos = { left: clampL(rect.left + rect.width / 2 - w / 2), top: rect.top + rect.height + PAD + 12 };
  } else if (rect.top - PAD >= cardH + 24) {
    pos = { left: clampL(rect.left + rect.width / 2 - w / 2), top: rect.top - PAD - 12 - cardH };
  } else {
    // A tall target (This month): the card sits low and centred, clear of its header and first rows.
    pos = { left: (vw - w) / 2, bottom: 16 };
  }
  const width = narrow ? vw - 32 : w;

  const hole = rect && `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${rect.top - PAD}px,
    ${rect.left - PAD}px ${rect.top - PAD}px, ${rect.left - PAD}px ${rect.top + rect.height + PAD}px,
    ${rect.left + rect.width + PAD}px ${rect.top + rect.height + PAD}px,
    ${rect.left + rect.width + PAD}px ${rect.top - PAD}px, 0 ${rect.top - PAD}px)`;

  return (
    <div className="sv-guide">
      <div className="sv-guide-dim" style={hole ? { clipPath: hole } : undefined} aria-hidden="true" />
      <div ref={cardRef} className="sv-guide-card" role="dialog" aria-modal="true" tabIndex={-1}
           aria-labelledby="sv-guide-title" aria-describedby="sv-guide-body"
           style={{ ...pos, width }} key={i}>
        <div className="sv-guide-top">
          <span className="sv-guide-count">Step {i + 1} of 4</span>
          <button type="button" className="sv-guide-skip" onClick={onClose}>Skip guide</button>
        </div>
        <h2 className="sv-guide-title" id="sv-guide-title">{step.t}</h2>
        <p className="sv-guide-body" id="sv-guide-body">{step.b}</p>
        <div className="sv-guide-btns">
          {i > 0 && <button type="button" className="sv-secondary" onClick={() => setI(i - 1)}>Back</button>}
          <button type="button" className="sv-primary" style={{ borderWidth: 2 }}
                  onClick={() => (i === 3 ? onClose() : setI(i + 1))}>{i === 3 ? 'Done' : 'Next'}</button>
        </div>
        <div className="sv-guide-dots" aria-hidden="true">
          {STEPS.map((s, n) => <span key={s.k} className={n === i ? 'on' : ''} />)}
        </div>
      </div>
    </div>
  );
}
